import type { Program } from "./schemas/program.js";
import type { CatalogMuscleSlug } from "./muscleCatalog.js";
import {
  type AggregatorExerciseRow,
  resolveExerciseMuscles,
} from "./volumeByMuscle.js";
import { resolveFullCycleSessions } from "./programResolution.js";
import {
  computeMuscleAdjacency,
  type AdjacencyDay,
  type MuscleAdjacencyWarning,
} from "./muscleAdjacency.js";
import { pickAuthoredText } from "./authoredText.js";
import type { Locale } from "./locale.js";

// Program adapter for the muscle-adjacency check. Expands the program's
// FULL repeating macrocycle (every week of the cycle, concatenated) into
// an ordered day sequence, resolving each day's stimulated muscles, then
// runs the cyclic core so the last day wraps onto the first — modelling
// the real calendar where day 5 (Sunday) is followed by day 1 (Monday).
//
// Each day's exercises come from resolveProgramSession — the SAME
// active-variant picker the logger's "Start session" flow runs — so a
// week's alternating / phase-offset variants resolve to the exercises
// actually trained that week. Two variants that never co-occur don't
// produce a false adjacency. Muscles come from resolveExerciseMuscles
// (the volume-math recruitment resolution) so the warning speaks the
// same muscle vocabulary as the volume map.
//
// A (session, week) cell with no active entries still occupies a position
// in the sequence: it's a scheduled day the athlete may rest, and keeping
// it preserves the true distance between the trained days around it.

export const programToAdjacencyDays = (
  program: Program,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
): AdjacencyDay[] => {
  const multiWeek = program.cycleWeeks > 1;
  return resolveFullCycleSessions(program, locale).map((resolved, position) => {
    const muscles = new Set<CatalogMuscleSlug>();
    for (const entry of resolved.entries) {
      const ex = exerciseBySlug.get(entry.exerciseId);
      if (!ex) continue;
      for (const m of resolveExerciseMuscles(ex)) muscles.add(m);
    }
    const dayName =
      pickAuthoredText(
        program.sessions[resolved.sessionIdx]?.name,
        locale,
        program.authorLocale,
      ) ?? `Day ${resolved.sessionIdx + 1}`;
    return {
      key: `w${resolved.weekIdx}s${resolved.sessionIdx}`,
      label: multiWeek ? `W${resolved.weekIdx + 1} ${dayName}` : dayName,
      position,
      muscles,
    };
  });
};

export const checkProgramMuscleAdjacency = (
  program: Program,
  exerciseBySlug: ReadonlyMap<string, AggregatorExerciseRow>,
  // Reader's language (PE-21): the program's authored text is resolved
  // reader → author → any available. Omitted → the chain starts at the
  // author, never at English.
  locale?: Locale,
): MuscleAdjacencyWarning[] => {
  const days = programToAdjacencyDays(program, exerciseBySlug, locale);
  return computeMuscleAdjacency(days, {
    cyclic: true,
    cycleLength: days.length,
  });
};
